
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CycleData, CyclePhase, getPhaseRecommendations, getCycleInsights, phaseColors } from '@/utils/cycle-tracking';
import { format, isToday, isWithinInterval, addDays } from 'date-fns';
import { Calendar, CalendarClock, AlertCircle, Check } from 'lucide-react';

interface CycleInsightsProps {
  cycleData: CycleData | null;
}

export const CycleInsights: React.FC<CycleInsightsProps> = ({ cycleData }) => {
  if (!cycleData) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center text-lg font-medium">
            <AlertCircle className="mr-2 h-5 w-5 text-hormony-coral" />
            No Cycle Data
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Please update your menstrual cycle information in your profile to see personalized insights.
          </p>
        </CardContent>
      </Card>
    );
  }

  const currentPhase = cycleData.currentPhase;
  const recommendations = getPhaseRecommendations(currentPhase);
  const insight = getCycleInsights(currentPhase);
  
  // Get today's cycle day
  const today = new Date();
  const todayCycleDay = cycleData.days.find(day => 
    isToday(day.absoluteDate)
  );

  const phaseColor = phaseColors[currentPhase];
  
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-medium">Current Cycle Status</CardTitle>
          <CardDescription>
            Your personalized hormone and cycle insights
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center text-sm">
                  <Calendar className="mr-2 h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Last period started:</span>
                  <span className="ml-2 font-medium">
                    {format(cycleData.periodStartDate, 'MMM d, yyyy')}
                  </span>
                </div>
                
                <div className="flex items-center text-sm">
                  <CalendarClock className="mr-2 h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Next period expected:</span>
                  <span className="ml-2 font-medium">
                    {format(cycleData.predictedNextPeriod, 'MMM d, yyyy')}
                  </span>
                </div>
                
                {todayCycleDay && (
                  <div className="flex items-center text-sm">
                    <div className="mr-2 h-4 w-4 rounded-full bg-hormony-coral"></div>
                    <span className="text-muted-foreground">Current cycle day:</span>
                    <span className="ml-2 font-medium">
                      Day {todayCycleDay.day} of {cycleData.cycleLength}
                    </span>
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <div 
                  className="flex items-center p-2 rounded-md" 
                  style={{ backgroundColor: `${phaseColor}50` }}
                >
                  <div className="mr-2 h-4 w-4 rounded-full" style={{ backgroundColor: phaseColor }}></div>
                  <span className="text-sm font-medium capitalize">
                    {currentPhase} Phase
                  </span>
                </div>
                
                <p className="text-xs text-muted-foreground">
                  {`You're ${cycleData.daysUntilNextPeriod} days away from your next predicted period.`}
                </p>
                
                {isWithinInterval(cycleData.predictedOvulation, {
                  start: today,
                  end: addDays(today, 3)
                }) && (
                  <div className="flex items-center p-2 bg-amber-50 text-amber-700 rounded-md">
                    <AlertCircle className="mr-2 h-4 w-4" />
                    <span className="text-xs font-medium">
                      Ovulation approaching in the next few days
                    </span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="mt-4 p-4 rounded-md bg-gray-50">
              <h4 className="text-sm font-medium mb-2">Hormonal Insight</h4>
              <p className="text-sm text-muted-foreground">{insight}</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-medium">Recommendations for Your Current Phase</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {recommendations.map((rec, idx) => (
              <li key={idx} className="flex items-start text-sm">
                <Check className="mr-2 h-4 w-4 text-hormony-coral mt-0.5 shrink-0" />
                <span>{rec}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};
